// Code for showing Ballot
import { Component, Fragment } from 'react';
import { savedUsername, fetchApi, subscribeToMasterBallotUpdates } from './utils';
import './Ballot.css';

const MIN_LENGTH_MS = 60 * 1000;
const HOUR_LENGTH_MS = 60 * MIN_LENGTH_MS;
const DAY_LENGTH_MS = 24 * HOUR_LENGTH_MS;

export const DEFAULT_SELECT_VALUE = '(none)';
export const BALLOT_URL = 'ballot';

type BallotProps = {
  username?: string,
}

type BallotState = {
  show?: string,
  response?: any,
  error?: string,
  username?: string,
  fetchedUsername?: string,
  // string[] to support multi-select in MasterBallot; regular Ballot always
  // stores single-element arrays.
  categories: Map<string, string[]>,
  hasChanges: boolean,
}

export type BallotCategory = {
  category: string,
  category_name: string,
  points: number,
  current_value?: string,
  nominees: string[],
  winner?: string[],
}

export default class Ballot extends Component<BallotProps, BallotState> {

  private unsubscribe?: () => void;
  private currentShow?: string;

  state: BallotState = { categories: new Map(), hasChanges: false };

  componentDidMount() {
    this.refreshBallot();

    // Listen for master ballot updates
    this.unsubscribe = subscribeToMasterBallotUpdates((data) => {
      if (data.show_id === this.currentShow) {
        // Refetch the ballot to get updated winners
        this.refreshBallot();
      }
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  // Builds the initial category→selection map from the API response.
  // Overridable so MasterBallot can seed from winners[] instead of current_value.
  buildCategoryMap(categories: BallotCategory[]): Map<string, string[]> {
    const categoryMap = new Map<string, string[]>();
    for (const category of categories) {
      categoryMap.set(
        category.category,
        category.current_value ? [category.current_value] : [DEFAULT_SELECT_VALUE],
      );
    }
    return categoryMap;
  }

  refreshBallot(username?: string) {
    username = username || this.getUsername();
    var url = this.getUrl(username);
    fetchApi(url)
      .then((responseJson) => {
        // Store the current show for use in event handlers
        this.currentShow = responseJson.show_id;
        this.setState({
          response: responseJson,
          username: username,
          fetchedUsername: username,
          categories: this.buildCategoryMap(responseJson.categories),
          show: responseJson.show_id,
          hasChanges: false,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ error: error.message });
      });
  }

  getUrl(username: string|undefined) {
    var url = BALLOT_URL;
    if (username) {
      url += `/${username}`;
    }
    return url;
  }

  getUsername() {
    return savedUsername();
  }

  getTopLevelClass() { return "page"; }

  getSubmitEndpoint() {
    return `submit/${this.state.username}`;
  }

  handleSubmit(event: any) {
    event.preventDefault();
    const ballotBody = { choices: new Array<{ category: string, choice: string }>() };
    this.state.categories.forEach((val, cat) => {
      if (val[0] !== DEFAULT_SELECT_VALUE) {
        ballotBody.choices.push({ category: cat, choice: val[0] });
      }
    });
    const options = {
      method: 'PUT',
      body: JSON.stringify(ballotBody),
      headers: {
        'Content-Type': 'application/json'
      }
    };
    fetchApi(this.getSubmitEndpoint(), this.state.show, options)
      .then(() => {
        this.setState({ hasChanges: false });
      })
      .catch((error) => {
        console.error(error);
      });
  }

  handleChange(event: any) {
    if (event.target.name === 'name') {
      this.setState({
        ...this.state,
        username: event.target.value,
        hasChanges: true
      });
      return;
    }
    this.setState({
      ...this.state,
      categories: this.state.categories.set(event.target.name, [event.target.value]),
      hasChanges: true
    })
  }

  usernameUpdated(event: any) {
    let trimmedUsername = this.state.username?.trim() || '';
    if (trimmedUsername === '' || trimmedUsername === this.state.fetchedUsername) {
      return;
    }
    this.setState({ username: trimmedUsername });
    document.cookie = `username=${trimmedUsername}`;
    this.refreshBallot(trimmedUsername);
  }

  // Determines if the select should be disabled for a category (can be overridden)
  shouldDisableCategory(category: BallotCategory) {
    return category.winner !== undefined;
  }

  renderCategory = (category: BallotCategory) => (
    <>
      <label className="awardname" htmlFor={category.category}>
        {category.category_name}
        <span className="pointvalue">
          ({category.points}&nbsp;pt{category.points > 1 ? 's' : ''})
        </span>
      </label>
      <div className="selection">
        <select name={category.category} id={category.category}
          value={this.state.categories.get(category.category)?.[0] || DEFAULT_SELECT_VALUE}
          onChange={(evt) => this.handleChange(evt)}
          disabled={this.shouldDisableCategory(category)}>
          <option value={DEFAULT_SELECT_VALUE}>{DEFAULT_SELECT_VALUE}</option>
          {category.nominees.map((nominee: string) => (
            <option value={nominee} key={nominee}>{nominee}</option>
          ))}
        </select>

      </div>
    </>
  )

  getNameField() {
    return <>
      <div className="name-field">
        <label className="entrantname" htmlFor="name">Name:</label>
        <input id="name" name="name" value={this.state.username || ''}
          onBlur={(evt) => this.usernameUpdated(evt)}
          onChange={(evt) => this.handleChange(evt)}></input>
      </div>
      <hr></hr>
    </>
  }

  renderBallotForm = () => (
    <form onSubmit={(evt) => this.handleSubmit(evt)}>
      {this.getNameField()}
      <div className="categories">
        {this.state.response.categories.map((category: BallotCategory) => (
          <Fragment key={category.category}>
            {this.renderCategory(category)}
          </Fragment>
        ))}
      </div>
      <div className="submit">
        <input type="submit" name="action" value="Save Changes" disabled={!this.state.hasChanges}></input>
      </div>
    </form>
  )

  render() {
    var topLevelClass = this.getTopLevelClass();
    if (this.state.error) {
      return <div className="error-message">Could not load ballot: {this.state.error}</div>
    }
    if (this.state.response == null) {
      return <div>Loading ballot...</div>
    }
    var totalPoints = 0;
    for (const category of this.state.response.categories) {
      totalPoints += category.points;
    }

    return <div className={topLevelClass}>
      <Header response={this.state.response} />
      <div id="ballot">
        {this.renderBallotForm()}
        <div className="scoring">
          <p><b>Scoring:</b> Each correct entry receives the number of
            points indicated, for a total of {totalPoints} points.</p>
        </div>
      </div>

      <div className="fineprint">
        Questions, complaints, death threats, etc. should be emailed
        to <a href="mailto:portnoy@portnoy.org">portnoy@portnoy.org</a>.
      </div>
    </div>
  }
}

function Header(props: { response: any }) {
  return <div id="header">
    <h1>{props.response.show_title}</h1>
    <div role="doc-subtitle">{timeToShow(props.response.show_date, props.response.show_id)}</div>
  </div>
}


function timeToShow(broadcastDate: string, showId?: string) {
  const showDate = new Date(broadcastDate);
  const now = Date.now();
  const diff = showDate.getTime() - now;
  // show standings link once we're within a day (including past)
  if (diff < DAY_LENGTH_MS) {
    const standingsUrl = showId ? `/standings?show=${showId}` : '/standings';
    return <span className="current-standings">View the <a href={standingsUrl}>current standings</a>!</span>;
  }
  const diffDays = Math.floor(diff / DAY_LENGTH_MS);
  if (diffDays > 0) {
    return `${diffDays}  day${diffDays > 1 ? 's' : ''} until the awards...`;
  }
  const diffHours = Math.floor(diff / HOUR_LENGTH_MS);
  if (diffHours > 1) {
    return `${diffHours} hours until the awards...`;
  }
  const diffMins = Math.floor(diff / MIN_LENGTH_MS);
  if (diffMins > 5) {
    return `${diffMins} minutes until the awards...`;
  }
  return "";
}
